import type { Thread, TargetAudiencePreset } from '../api/types';
import { normalizeKeyPeople } from '../api/keyPeople';
import styles from './ThreadContextCard.module.css';

interface ThreadContextCardProps {
  thread: Thread;
  templateName?: string;
  cumulativeSummary?: string | null;
}

// Display labels for the target-audience preset shown to contributors so they
// know who the story is for. Keep in sync with storythreads-api
// constants/targetAudience.ts (TARGET_AUDIENCE_LABELS).
const TARGET_AUDIENCE_LABELS: Record<TargetAudiencePreset, string> = {
  just_us: 'Just us',
  close_family: 'Close family & friends',
  kids_present: 'Kids in the room',
  broad_public: 'Sharing far & wide',
  formal_keepsake: 'A formal keepsake',
};

const THREAD_TYPE_LABELS: Record<string, string> = {
  memory: 'Throwback',
  event: 'Event',
  topic: 'Topic',
  milestone: 'Milestone',
  legacy: 'Origin Story',
  quest: 'Quest',
  freeform: 'Open',
  template: 'Guided',
};

const THREAD_TYPE_DESCRIPTIONS: Record<string, string> = {
  memory: 'A shared memory told from multiple perspectives',
  event: 'A shared experience captured from different angles',
  legacy: 'A life story told through many voices',
  topic: 'An open discussion from different viewpoints',
  milestone: 'A milestone celebrated by the people who were there',
  quest: 'A collaborative quest with guided steps',
  freeform: 'An open story with no set structure',
  template: 'A guided story following a structured template',
};

const AVATAR_COLORS = [
  '#D97706', '#059669', '#7C3AED', '#DC2626', '#2563EB',
  '#DB2777', '#0891B2', '#65A30D', '#9333EA', '#EA580C',
];

function getInitials(name: string): string {
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

function getAvatarColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash << 5) - hash + id.charCodeAt(i);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function formatMemoryDate(date: string, endDate?: string | null): string {
  const fmt = (d: string) => new Date(d).toLocaleDateString(undefined, {
    month: 'long', day: 'numeric', year: 'numeric',
  });
  if (endDate) {
    return `${fmt(date)} – ${fmt(endDate)}`;
  }
  return fmt(date);
}

export function ThreadContextCard({ thread, templateName, cumulativeSummary }: ThreadContextCardProps) {
  const typeLabel = thread.threadType ? THREAD_TYPE_LABELS[thread.threadType] ?? thread.threadType : null;
  const hasContent = thread.description || typeLabel || thread.creator || templateName || thread.introVideoUrl || thread.currentPhase || cumulativeSummary;

  if (!hasContent) return null;

  return (
    <div class={styles.heroCard}>
      <div class={styles.heroHeader}>
        {typeLabel && <span class={styles.typeBadge}>{typeLabel}</span>}
        {thread.participantCount > 0 && (
          <span class={styles.statBadge}>
            {thread.participantCount} {thread.participantCount === 1 ? 'perspective' : 'perspectives'}
          </span>
        )}
        {thread.contributionCount > 0 && (
          <span class={styles.statBadge}>
            {thread.contributionCount} {thread.contributionCount === 1 ? 'take' : 'takes'}
          </span>
        )}
      </div>
      {thread.threadType && THREAD_TYPE_DESCRIPTIONS[thread.threadType] && (
        <p class={styles.typeDescription}>{THREAD_TYPE_DESCRIPTIONS[thread.threadType]}</p>
      )}

      {thread.introVideoUrl && (
        <video
          class={styles.heroVideo}
          src={thread.introVideoUrl}
          controls
          preload="metadata"
          playsInline
          poster={thread.introVideoThumbnailUrl ?? undefined}
        />
      )}

      {thread.description && (
        <p class={styles.heroDescription}>{thread.description}</p>
      )}

      {thread.plotSummary && (
        <div class={styles.plotSummaryContainer}>
          <div class={styles.plotSummaryHeader}>
            <svg class={styles.plotSummaryIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
            </svg>
            <span class={styles.plotSummaryTitle}>The Plot</span>
          </div>
          <p class={styles.plotSummaryText}>{thread.plotSummary}</p>
        </div>
      )}

      {thread.storyContext?.backstory && (
        <div class={styles.storyContextContainer}>
          <div class={styles.storyContextHeader}>
            <svg class={styles.storyContextIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
              <path d="M21 5c-1.11-.35-2.33-.5-3.5-.5-1.95 0-4.05.4-5.5 1.5-1.45-1.1-3.55-1.5-5.5-1.5S2.45 4.9 1 6v14.65c0 .25.25.5.5.5.1 0 .15-.05.25-.05C3.1 20.45 5.05 20 6.5 20c1.95 0 4.05.4 5.5 1.5 1.35-.85 3.8-1.5 5.5-1.5 1.65 0 3.35.3 4.75 1.05.1.05.15.05.25.05.25 0 .5-.25.5-.5V6c-.6-.45-1.25-.75-2-1z" />
            </svg>
            <span class={styles.storyContextTitle}>What to Know</span>
          </div>
          <p class={styles.storyContextText}>{thread.storyContext.backstory}</p>
        </div>
      )}

      {normalizeKeyPeople(thread.storyContext?.keyPeople).length > 0 && (
        <div class={styles.keyPeopleRow}>
          {normalizeKeyPeople(thread.storyContext?.keyPeople).map(person => (
            <span key={person.name} class={styles.keyPersonBadge}>
              {person.name}
              {person.role && <span class={styles.keyPersonRole}> · {person.role}</span>}
            </span>
          ))}
        </div>
      )}

      {thread.storyContext?.setting && (
        <p class={styles.storySetting}>
          {thread.storyContext.setting}
        </p>
      )}

      {thread.storyContext?.targetAudience && (
        <p class={styles.storySetting}>
          <span class={styles.keyPersonBadge}>
            For {TARGET_AUDIENCE_LABELS[thread.storyContext.targetAudience.preset]
              ?? thread.storyContext.targetAudience.preset}
          </span>
          {thread.storyContext.targetAudience.note && (
            <span class={styles.keyPersonRole}> · {thread.storyContext.targetAudience.note}</span>
          )}
        </p>
      )}

      {thread.storyContext?.contributorGuidance && (
        <p class={styles.contributorGuidance}>
          {thread.storyContext.contributorGuidance}
        </p>
      )}

      {thread.memoryDate && (
        <p class={styles.memoryDate}>
          From {formatMemoryDate(thread.memoryDate, thread.memoryEndDate)}
        </p>
      )}

      {cumulativeSummary && (
        <div class={styles.plotSummaryContainer}>
          <div class={styles.plotSummaryHeader}>
            <svg class={styles.plotSummaryIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
            </svg>
            <span class={styles.plotSummaryTitle}>Story So Far</span>
          </div>
          <p class={styles.plotSummaryText}>{cumulativeSummary}</p>
        </div>
      )}

      {/* Show context about the specific phase currently running */}
      {thread.currentPhase && (
        <div class={styles.phaseContext}>
          <h4 class={styles.phaseContextTitle}>{thread.currentPhase.name}</h4>
          {thread.currentPhase.description && (
            <p class={styles.phaseContextDesc}>{thread.currentPhase.description}</p>
          )}
        </div>
      )}

      <div class={styles.metaRow}>
        {thread.creator && (
          <span class={styles.creatorInfo}>
            <span
              class={styles.creatorAvatar}
              style={{ backgroundColor: getAvatarColor(thread.creator.id) }}
            >
              {getInitials(thread.creator.displayName)}
            </span>
            Started by {thread.creator.displayName}
          </span>
        )}
        {thread.creator && templateName && <span class={styles.dot}>&middot;</span>}
        {templateName && (
          <span class={styles.templateTag}>{templateName}</span>
        )}
      </div>
    </div>
  );
}
